/**
 * Whether this session may mint a study-group mirror: its PERSONAL store has
 * caught up with the server (the UserDo sync status is `ready`: a handshake's
 * diff has been applied, and nothing since has taken the socket down), or the
 * server has been out of reach for a while.
 *
 * Minting a study-group mirror reads it (`study-groups/mirror-seed.ts`). The
 * group socket is a small DO and usually syncs first, so a device whose disk
 * predates a mirror minted elsewhere — a second browser, a phone that last
 * synced before the deck was shared — saw "no mirror" and minted a twin; the
 * real one arrived in the diff seconds later, with the member's history on it.
 *
 * Out of reach: a device offline, or whose personal sync failed, never gets to
 * `ready`, and waiting for it left a group deck it had no mirror for on a
 * spinner for good. After {@link UNREACHABLE_MINT_MS} not ready and not mid
 * handshake (`syncing`: a large account's diff is on its way), the mint goes
 * ahead as it did before the gate; a twin it makes is merged by the server
 * once the device is back (`party/server/user/sg-twin-merge.ts`).
 *
 * A module flag rather than the sync context: the mint also runs under the
 * study-group orchestrator, which renders outside the context provider.
 */
import { useSyncExternalStore } from 'react';

import type { SyncStatus } from './hook/sync-context-types';

/** How long the server may be out of reach before a mint stops waiting. */
export const UNREACHABLE_MINT_MS = 20_000;

let ready = false;
let syncing = false;
/** Since when this session has not been `ready`; null while it is, or before
 *  any session started. */
let notReadySince: number | null = null;
let open = false;
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function update(): void {
  if (timer) clearTimeout(timer);
  timer = null;
  const waiting = !ready && !syncing && notReadySince !== null;
  const waited = waiting ? Date.now() - notReadySince! : 0;
  const next = ready || (waiting && waited >= UNREACHABLE_MINT_MS);
  if (waiting && !next) {
    timer = setTimeout(update, UNREACHABLE_MINT_MS - waited);
    // Never keeps a Node process (a test run) alive on its own.
    (timer as { unref?: () => void }).unref?.();
  }
  if (next === open) return;
  open = next;
  for (const listener of listeners) listener();
}

/** The UserDo sync status, as the sync context sets it. */
export function setPersonalSyncStatus(status: SyncStatus): void {
  ready = status === 'ready';
  syncing = status === 'syncing';
  if (ready) notReadySince = null;
  else notReadySince ??= Date.now();
  update();
}

/** `true`: the store is reconciled. `false`: a new session (a user switch) or
 *  none (unbind), not ready from now. */
export function setPersonalSyncReady(value: boolean): void {
  if (value) {
    setPersonalSyncStatus('ready');
    return;
  }
  ready = false;
  syncing = false;
  notReadySince = Date.now();
  update();
}

export function isPersonalSyncReady(): boolean {
  return open;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** {@link isPersonalSyncReady}, re-rendering when it flips. */
export function usePersonalSyncReady(): boolean {
  return useSyncExternalStore(subscribe, isPersonalSyncReady, isPersonalSyncReady);
}
